import {
  convertFAMs,
  getValidActions,
} from "utils/wizardHelpers/wizardHelpers";
import { computedZone } from "components/Topology/computedLayout/computedZone";
import { topologyConfig } from "../configs/topologyConfig";
import { topologyColors } from "../configs/topologyColors";

const { gray400 } = topologyColors;
const { addBtnMarginTop, rightChildrenGapX, zoneTitleToLineGap } =
  topologyConfig;

/**
 * @param {object} params
 * @param {*} [params.structuredTopology]
 * @param {*} [params.subType]
 * @param {number | null} [params.selectedZoneIdx]
 * @param {boolean} [params.isPrint]
 */
export const getComputedLayout = ({
  structuredTopology,
  subType,
  selectedZoneIdx = null,
  isPrint = false,
}) => {
  if (!structuredTopology) {
    return {
      nodes: [],
      lines: [],
      twoAngleLines: [],
      bottomLeftAngleLines: [],
      leftBracketPolyLines: [],
      rects: [],
      texts: [],
      interactiveElementsArr: [],
      width: 0,
      height: 0,
    };
  }

  const {
    sameLayer,
    bottomPlacedChildren,
    connectedByTopBracketChildren,
    ...systemController
  } = structuredTopology;

  let topologyWidth = 0;
  let topologyHeight = 0;

  const setTopologyWidth = (w) => {
    topologyWidth = Math.max(topologyWidth, w);
  };

  const setTopologyHeight = (h) => {
    topologyHeight = Math.max(topologyHeight, h);
  };

  const {
    startTopologyY,
    startTopologyX,
    nodeLinesGap,
    nodeWidth,
    nodeHeight,
    zoneMarginTop,
    zoneMarginTopWireless,
    zoneDefaultWidth,
    zoneNodeMarginBottom,
    zoneTopBracketHeight,
    addBtnHeight,
    addBtnWidth,
    addBtnMarginLeft,
  } = topologyConfig;

  const nodes = [];
  const lines = [];
  const bottomLeftAngleLines = [];
  const leftBracketPolyLines = [];
  const twoAngleLines = [];
  const rects = [];
  const texts = [];
  const interactiveElementsArr = [];

  let height = startTopologyY;
  const scStartY = startTopologyY;

  if (!!selectedZoneIdx && isPrint) {
    // only one zone
    const famData =
      bottomPlacedChildren[0].connectedByTopBracketChildren[
        selectedZoneIdx - 1
      ];
    const zone = computedZone({
      isPrint,
      subType,
      famStartX: startTopologyX,
      famStartY: startTopologyY + zoneTitleToLineGap + 22,
      famData,
      interactiveElementsArr,
      setTopologyWidth,
      setTopologyHeight,
      topologyHeight,
      zoneIdx: selectedZoneIdx,
    });

    nodes.push(...zone.nodes);
    texts.push(...zone.texts);
    twoAngleLines.push(...zone.twoAngleLines);
    interactiveElementsArr.push(...zone.interactiveElementsArr);
    bottomLeftAngleLines.push(...zone.bottomLeftAngleLines);
    leftBracketPolyLines.push(...zone.leftBracketPolyLines);
    setTopologyWidth(zoneDefaultWidth);
    setTopologyHeight(Math.max(topologyHeight, zone.zoneHeight));
  } else {
    setTopologyWidth(rightChildrenGapX);
    //  System Controller
    nodes.push({
      id: systemController.id,
      kind: "system_controller",
      title: systemController.title,
      subTitle: systemController.presentation,
      iconName: systemController.iconName,
      x: startTopologyX,
      y: scStartY,
      isPrint,
    });
    interactiveElementsArr.push({
      ...systemController,
      interactiveKind: "node",
      startX: startTopologyX,
      startY: scStartY,
    });
    height += nodeHeight + 32;

    //  BMS sameLayer
    if (sameLayer) {
      sameLayer.forEach((item) => {
        const startX = isPrint
          ? startTopologyX
          : startTopologyX + nodeWidth + 60;
        nodes.push({
          id: item.id,
          kind: "squareNode",
          title: item.title,
          subTitle: item.presentation,
          iconName: item.iconName,
          x: startX,
          y: isPrint ? height : scStartY,
          isPrint,
        });
        lines.push({
          from: {
            x: isPrint ? startTopologyX + nodeHeight / 2 : startX,
            y: isPrint ? height : scStartY + nodeHeight / 2,
          },
          to: {
            x: isPrint
              ? startTopologyX + nodeHeight / 2
              : startTopologyX + nodeWidth,
            y: isPrint
              ? startTopologyY + nodeHeight
              : scStartY + nodeHeight / 2,
          },
        });
      });
    }

    //  Switchers
    if (bottomPlacedChildren) {
      const switcherStartX = isPrint ? nodeWidth + 64 : startTopologyX;
      const switcherStartY = isPrint
        ? startTopologyY
        : (height += zoneNodeMarginBottom - 32);

      lines.push({
        from: {
          x: isPrint
            ? startTopologyX + nodeWidth
            : startTopologyX + nodeHeight / 2,
          y: isPrint ? switcherStartY + nodeHeight / 2 : scStartY + nodeHeight,
        },
        to: {
          x: isPrint
            ? switcherStartX + nodeHeight / 2
            : switcherStartX + nodeHeight / 2,
          y: isPrint ? switcherStartY + nodeHeight / 2 : switcherStartY,
        },
      });

      bottomPlacedChildren.forEach((switcher) => {
        nodes.push({
          id: switcher.id,
          kind: "switch",
          isSwitch: true,
          title: switcher.title,
          subTitle: switcher.presentation,
          iconName: switcher.iconName,
          x: switcherStartX,
          y: switcherStartY,
          isPrint,
        });

        interactiveElementsArr.push({
          ...switcher,
          interactiveKind: "node",
          startX: switcherStartX,
          startY: switcherStartY,
        });

        height += isPrint
          ? nodeHeight + 32
          : nodeHeight + 32 + zoneNodeMarginBottom;

        //  FAM Groups
        if (switcher?.connectedByTopBracketChildren) {
          const splitByGroupsFAMs = convertFAMs(
            switcher.connectedByTopBracketChildren,
          );
          let startX = switcherStartX;
          const startY = isPrint
            ? nodeHeight * 2 + 64
            : nodeHeight * 5.5 + zoneMarginTop;

          splitByGroupsFAMs.forEach((famGroup, famGroupIdx) => {
            famGroup.forEach((fam, famIdx) => {
              const zone = computedZone({
                isPrint,
                famStartX: startX,
                famStartY: startY,
                famData: fam,
                interactiveElementsArr,
                setTopologyHeight,
                topologyHeight,
                zoneIdx: fam.zoneIdx,
              });

              nodes.push(...zone.nodes);
              texts.push(...zone.texts);
              twoAngleLines.push(...zone.twoAngleLines);
              interactiveElementsArr.push(...zone.interactiveElementsArr);
              bottomLeftAngleLines.push(...zone.bottomLeftAngleLines);
              leftBracketPolyLines.push(...zone.leftBracketPolyLines);
              setTopologyWidth(topologyWidth + zoneDefaultWidth);
              setTopologyHeight(Math.max(topologyHeight, zone.zoneHeight));

              // Add buttons above zone
              if (!isPrint) {
                const { isPasteEnabled } = getValidActions(fam.menu);
                const addBtn = fam.menu?.find((btn) => btn.id === 10);
                if (addBtn?.submenu.length || isPasteEnabled) {
                  const addBtnX = startX + addBtnHeight + addBtnMarginLeft;
                  const addBtnY =
                    startY - zoneTopBracketHeight - addBtnHeight + 10;

                  interactiveElementsArr.push({
                    interactiveKind: isPasteEnabled ? "pastBtn" : "addBtn",
                    startX: addBtnX,
                    startY: addBtnY,
                    submenu: addBtn?.submenu || [],
                    famGroupIdx,
                    topologyNodeId: fam.id,
                    topologyAction: 10,
                    isPasteEnabled,
                  });

                  lines.push({
                    from: { x: addBtnX, y: addBtnY },
                    to: { x: startX + nodeHeight / 2, y: addBtnY },
                    stroke: gray400,
                  });
                  lines.push({
                    from: { x: startX + nodeHeight / 2, y: addBtnY },
                    to: { x: startX + nodeHeight / 2, y: startY },
                    stroke: gray400,
                  });
                }
              }

              // Connection lines between FAMs
              if (famIdx === 0) {
                lines.push({
                  from: {
                    x: startX + nodeHeight / 2 - nodeLinesGap,
                    y: startY,
                  },
                  to: {
                    x: startX + nodeHeight / 2 - nodeLinesGap,
                    y: switcherStartY + nodeHeight + 18,
                  },
                });
              } else {
                lines.push({
                  from: {
                    x: startX + nodeHeight / 2 - nodeLinesGap,
                    y: startY,
                  },
                  to: {
                    x: startX + nodeHeight / 2 - nodeLinesGap,
                    y: startY - zoneTopBracketHeight,
                  },
                });
                lines.push({
                  from: {
                    x: startX + nodeHeight / 2 - nodeLinesGap,
                    y: startY - zoneTopBracketHeight,
                  },
                  to: {
                    x:
                      startX + nodeHeight / 2 - zoneDefaultWidth + nodeLinesGap,
                    y: startY - zoneTopBracketHeight,
                  },
                });
                lines.push({
                  from: {
                    x:
                      startX + nodeHeight / 2 + nodeLinesGap - zoneDefaultWidth,
                    y: startY,
                  },
                  to: {
                    x:
                      startX + nodeHeight / 2 + nodeLinesGap - zoneDefaultWidth,
                    y: startY - zoneTopBracketHeight,
                  },
                });
              }

              startX += zoneDefaultWidth;
            });
          });
        }

        // Draw connection bus
        rects.push({
          x: switcherStartX - 1,
          y: isPrint
            ? startTopologyY + nodeHeight
            : switcherStartY + nodeHeight,
          styles: {
            width: topologyWidth,
            height: 20,
            fill: "#dee0e5",
          },
        });
        // Draw text on the bus
        texts.push({
          x: switcherStartX + 16,
          y: isPrint
            ? startTopologyY + nodeHeight + 14
            : switcherStartY + nodeHeight + 14,
          text: switcher.info,
          styles: {
            fz: 16,
            fw: "bold",
            color: "#4f5155",
          },
        });

        // Add Zone button on bus
        if (!isPrint) {
          const { isPasteEnabled } = !isPrint && getValidActions(switcher.menu);

          const addBtn = switcher.menu?.find((btn) => btn.id === 10);

          if (addBtn?.submenu.length || isPasteEnabled) {
            const addBtnX = topologyWidth - addBtnWidth - 10;
            const addBtnY =
              height / 2 + nodeHeight + 20 + addBtnHeight / 2 + addBtnMarginTop;

            interactiveElementsArr.push({
              interactiveKind: isPasteEnabled ? "pastBtn" : "addBtn",
              startX: addBtnX,
              startY: addBtnY,
              submenu: addBtn?.submenu || [],
              topologyNodeId: switcher.id,
              topologyAction: 10,
              isPasteEnabled,
            });

            lines.push({
              from: { x: addBtnX, y: addBtnY },
              to: { x: addBtnX - addBtnMarginLeft, y: addBtnY },
              stroke: gray400,
            });
            lines.push({
              from: { x: addBtnX - addBtnMarginLeft, y: addBtnY },
              to: {
                x: addBtnX - addBtnMarginLeft,
                y: height / 2 + nodeHeight + 4,
              },
              stroke: gray400,
            });
          }
        }

        // Draw X-Cube
        if (switcher.sameLayer) {
          switcher.sameLayer.forEach((item, sameLayerIdx) => {
            const startX = (switcherStartX + 600) * (sameLayerIdx + 1);
            nodes.push({
              ...item,
              id: item.id,
              kind: "squareNode",
              title: item.title,
              subTitle: item.presentation,
              iconName: item.iconName,
              x: startX,
              y: isPrint ? startTopologyY : height / 2 - nodeHeight,
              isPrint,
            });
            lines.push({
              from: {
                x: isPrint
                  ? switcherStartX + nodeWidth
                  : startX + nodeHeight / 2,
                y: isPrint ? startTopologyY + nodeHeight / 2 : height / 2 + 21,
              },
              to: {
                x: isPrint ? startX : startX + nodeHeight / 2,
                y: isPrint
                  ? startTopologyY + nodeHeight / 2
                  : height / 2 + nodeHeight + 1,
              },
            });
            height += zoneNodeMarginBottom;
          });
        }
      });
    }

    //  Wireless Zones
    if (connectedByTopBracketChildren) {
      let startX = startTopologyX;
      const startY = isPrint
        ? nodeHeight * 2 + 64
        : nodeHeight + zoneMarginTopWireless;

      connectedByTopBracketChildren.forEach((fam, famIdx) => {
        const zone = computedZone({
          isPrint,
          famStartX: startX,
          famStartY: startY,
          famData: fam,
          interactiveElementsArr,
          setTopologyWidth,
          setTopologyHeight,
          topologyHeight,
          zoneIdx: fam.zoneIdx,
        });

        nodes.push(...zone.nodes);
        texts.push(...zone.texts);
        twoAngleLines.push(...zone.twoAngleLines);
        interactiveElementsArr.push(...zone.interactiveElementsArr);
        bottomLeftAngleLines.push(...zone.bottomLeftAngleLines);
        leftBracketPolyLines.push(...zone.leftBracketPolyLines);
        setTopologyWidth(topologyWidth + zoneDefaultWidth);
        setTopologyHeight(Math.max(topologyHeight, zone.zoneHeight));

        // Add wireless add button and lines
        if (!isPrint) {
          const { isPasteEnabled } = getValidActions(fam.menu);
          const addBtn = fam.menu?.find((btn) => btn.id === 10);
          if (addBtn?.submenu?.length || isPasteEnabled) {
            const addBtnX = startX + zoneDefaultWidth;
            const addBtnY = startY + addBtnHeight / 2 - 8;
            interactiveElementsArr.push({
              interactiveKind: isPasteEnabled ? "pastBtn" : "addBtn",
              startX: addBtnX,
              startY: addBtnY,
              submenu: addBtn?.submenu || [],
              topologyNodeId: fam.id,
              topologyAction: 10,
              isPasteEnabled,
            });
            lines.push({
              from: {
                x: startX + addBtnHeight / 2,
                y: startY - zoneMarginTopWireless / 2,
              },
              to: {
                x: addBtnX + addBtnWidth / 2 - 12,
                y: startY - zoneMarginTopWireless / 2,
              },
              isWireless: true,
            });
            lines.push({
              from: {
                x: addBtnX + addBtnWidth / 2 - 12,
                y: addBtnY - addBtnHeight / 2 + 6,
              },
              to: {
                x: addBtnX + addBtnWidth / 2 - 12,
                y: startY - zoneMarginTopWireless / 2,
              },
              isWireless: true,
            });
          }
        }

        // wireless connecting line
        if (famIdx === 0) {
          lines.push({
            from: {
              x: startX + nodeHeight / 2,
              y: scStartY + nodeHeight,
            },
            to: {
              x: startX + nodeHeight / 2,
              y: startY,
            },
            isWireless: true,
          });
        } else {
          lines.push({
            from: {
              x: startX + nodeHeight / 2,
              y: startY - zoneMarginTopWireless / 2,
            },
            to: {
              x: startX + nodeHeight / 2 - zoneDefaultWidth,
              y: startY - zoneMarginTopWireless / 2,
            },
            isWireless: true,
          });
          lines.push({
            from: {
              x: startX + nodeHeight / 2,
              y: startY,
            },
            to: {
              x: startX + nodeHeight / 2,
              y: startY - zoneMarginTopWireless / 2,
            },
            isWireless: true,
          });
        }

        startX += zoneDefaultWidth;
      });
    }
  }

  return {
    nodes,
    lines,
    twoAngleLines,
    bottomLeftAngleLines,
    leftBracketPolyLines,
    rects,
    texts,
    interactiveElementsArr,
    width: topologyWidth,
    height: topologyHeight,
  };
};
